import "./styles/index.scss";

import { useState, memo, useEffect } from "react";
import {
  selectActiveWinery,
  selectAnalysis,
  selectConfigs,
  selectDrugs,
  selectStoredAnalysisResults,
  selectTreatments,
  selectUnits,
  selectUsers,
  selectWineMaking,
} from "../../../context/selectors";
import { useDispatch, useSelector } from "react-redux";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useLabels from "../../../hooks/useLabels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getDrugs,
  getLotTracking,
  getProtocols,
  getTankTracking,
  getUnits,
  getUsers,
} from "../../../services/utils";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { setUnits } from "../../../context/units/unitsSlice";
import { setUsers } from "../../../context/users/usersSlice";
import { setDrugs } from "../../../context/substances/drugsSlice";
import { setTreatments } from "../../../context/protocols/treatmentsSlice";
import { setAnalysis } from "../../../context/protocols/analysisSlice";

import { format } from "date-fns";
import Loader from "../../Loader";
import { CTA } from "../../Generic";
import { DestTankDetail } from "../../OperationsDetail/Details";
import { computeFlowRates, getElapsedTimeWithClass } from "../../../services/common";
import { setWineMaking } from "../../../context/protocols/wineMakingSlice";

// Single timeline element
const TimelineElement = memo(({ id, op, type, units, protocols, onToggle, isOpen }) => {
  
  const [getLabel] = useLabels();
  const configs = useSelector(selectConfigs);
  
  const isSrc = op?.src_tanks?.find((t) => type === "lot" ? t.batch_id === Number(id) : t.tank_id === Number(id));
  const src = isSrc
    ? isSrc
    : op?.dest_tanks?.find((t) => type === "lot" ? t.batch_id === Number(id) : t.tank_id === Number(id));
  
  const isCurrentStateOnly = (
    [
      "ADD", 
      "OXYGENATION", 
      "HOMOGENIZATION_MASS", 
      "CLEANING", 
      "TREATMENT", 
      "ANALYSIS", 
      "STABILIZATION_STATIC", 
      "STABILIZATION_DYNAMIC", 
      "STABILIZATION_RESIN",
    ].includes(op?.operation_type) 
    // && type === "tank"
  );
  const handleClick = () => onToggle(op.operation_id);

  const getIconByOperationType = (type) => {
    switch (type) {
      case "TREATMENT":
        return "fa-prescription-bottle";
      case "ANALYSIS":
        return "fa-flask-vial";
      case "RACKING":
        return "fa-sitemap";
      case "CUT":
        return "fa-sitemap";
      case "DECANT":
        return "fa-sitemap";
      case "DESTEMMING":
        return "fa-cubes-stacked";
      case "BOTTLING":
        return "fa-wine-bottle";
      case "NEW_LOT":
        return "fa-tent-arrow-down-to-line";
      case "CLEANING":
        return "fa-soap";
      case "STABILIZATION_STATIC":
      case "STABILIZATION_DYNAMIC":
        return "fa-snowflake";
      case "STABILIZATION_RESIN":
        return "fa-bezier-curve";
      case "FILTERING":
        const filteringType = op?.treatments?.[0]?.type;
        switch (filteringType) {
          case "HOUSING": return "fa-bars";
          case "CARTON": return "fa-layer-group";
          case "TANGENTIAL": return "fa-sliders";
          default: return "fa-filter";
        };
      default:
        return "";
    }
  };

  const computeDrop = (isOperationDrop = false) => {
    let drop = 0;

    const dropLabel = getLabel(
      (isSrc || op?.dest_tanks?.length > 1) && !isOperationDrop
        ? "operationTankDrop"
        : "operationDrop");
    const dropUnit = op?.operation_type === "DESTEMMING" && isOperationDrop ? "Kg" : "L";
    
    if (isOperationDrop) {
      const pomaces = op?.treatments
        ?.reduce((acc, curr) => {
          return acc + (Number(curr?.quantity) || 0);
        }, 0) || 0;
        const pomacesType = op?.treatments?.[0]?.waste_type ? `(${getLabel(`pomaceReason_${op?.treatments?.[0]?.waste_type}`)?.toLowerCase?.()})` : "";
        return { drop: pomaces, dropUnit, dropLabel: `${dropLabel} ${pomacesType}` };
    }
    
    if (isSrc) {
      if (op?.operation_type === "BOTTLING") {
        drop =
          op?.dest_tanks
            ?.reduce((acc, curr) => {
              return acc + (Number(curr?.drop) || 0);
            }, 0) || 0;
        return { drop, dropUnit, dropLabel };
      }

      return { drop: 0 };
    }

    if (op?.operation_type === "CUT") {
      // TO DO check
      drop = Number(src?.drop) || 0;
    } else {
      drop =
        op?.dest_tanks
          ?.filter((t) => type === "lot" || t.tank_id === Number(id))
          ?.reduce((acc, curr) => {
            return acc + (Number(curr?.drop) || 0);
          }, 0) || 0;
    }

    return { drop, dropUnit, dropLabel };
  };

  const renderOperationRecap = () => {
    if (type === "lot") return;
    
    // if (
    //   [
    //     "RACKING",
    //     "CUT",
    //     "DECANT",
    //     "DESTEMMING",
    //     "NEW_LOT",
    //     "FILTERING",
    //     "CLOSING",
    //   ].includes(op?.operation_type) &&
    //   type === "tank"
    // )

      const tmpTank = { ...src };
      if (op?.operation_type === "STABILIZATION_RESIN") {
        tmpTank.is_planned = true;
        tmpTank.planned_quantity = tmpTank.old_quantity;
        tmpTank.old_quantity = Number(tmpTank.old_quantity) * Number(op?.treatments?.[0]?.quantity || 0) / 100;
      } else if (op?.operation_type === "RACKING") {
        // tmpTank.planned_quantity = tmpTank.old_quantity;
        if (src?.is_src) tmpTank.arrow_quantity += Number(op?.treatments?.[0]?.quantity || 0);
      }

      return (
        <DestTankDetail
          tank={tmpTank}
          operation={op}
          isName={false}
          isDrop={false}
          isShowComposition={false}
          isCurrentStateOnly={isCurrentStateOnly}
          // isMobile={isMobile}
        ></DestTankDetail>
      );  
  };

  const getExamResults = (isAnalysis, isTreatment, trs = []) => {
    
    // find the exam for this tank/batch
    const exam = trs.find((t, i) =>
      type === "tank"
        ? t.tank_id === Number(id)
        : i === 0 || t.batch_id === Number(id)
    );
    if (!exam) return { protocol: null, exam: null, results: null };

    const protocol =
      protocols?.find((a) => a.protocols_id === exam.protocol_id) || null;
    if (!protocol) return { protocol: null, exam: null, results: null };

    // Use a Map keyed by "batch_id|substance_id" to get unique treatments in case of type = lot
    const results = (isAnalysis ? exam?.results : isTreatment ? 
      Array.from(
      new Map(
        trs?.filter(t => type === "lot" || true)?.map(item => [`${item.batch_id}|${item.substance_id}`, item])
      ).values()
    ) : trs) || [];

    return { protocol, exam, results }
  }

  const renderAnalysisBlock = () => {
    const isAnalysis = op?.operation_type === "ANALYSIS";
    const isTreatment = op?.operation_type === "TREATMENT";
    const trs = op?.treatments || [];
    if (!trs.length) return null;

    const { protocol, exam, results } = getExamResults(isAnalysis, isTreatment, trs);
    
    if (!protocol || !exam || !results) return;

    // Link each result with its corresponding TR definition
    const detailedSubstances = results?.map((res) => {
      const trDef = trs.find((t) => t.substance_id === res.substance_id);

      const min = trDef?.min_range ?? null;
      const max = trDef?.max_range ?? null;

      const isInside =
        (min === null || res.value >= min) &&
        (max === null || res.value <= max);

      return {
        substance_id: res.substance_id,
        name: isAnalysis ? getLabel(`substance_${trDef?.substance_name}`) : trDef?.substance_name,
        value: res.value,
        unit: trDef?.unit || units?.find((u) => u.id == trDef?.unit_id)?.unit || "",
        quantity: trDef?.quantity || "-",
        ...(isAnalysis && {
          min,
          max,
          isInside,
        }),
      };
    });
    
    return (
      <>

        {/* PROTOCOL */}
        <div className="tank-details-row">
          <span className="label">{getLabel(isAnalysis ? "analysisProtocols" : "treatmentProtocols")}</span>
          <span className="value">{protocol.protocols_name}</span>
        </div>

        {/* COLLECTION DATE */}
        {isAnalysis && <div className="tank-details-row">
          <span className="label">{getLabel("dateCollection")}</span>
          <span className="value">
            {exam.collection_date
              ? format(new Date(exam.collection_date), configs.hourMediumDateFormat)
              : "-"}
          </span>
        </div>}

        {/* ANALYST */}
        {isAnalysis && <div className="tank-details-row">
          <span className="label">{getLabel("analysisAnalyst")}</span>
          <span className="value">{exam.analyst || "-"}</span>
        </div>}

        {/* TEMPERATURE */}
        {isAnalysis && <div className="tank-details-row">
          <span className="label">{getLabel("analysisTemperature")}</span>
          <span className="value">{`${exam.temperature || ""}${"\u00b0"} C`}</span>
        </div>}

        {/* RESULTS */}
        <div className="tank-details-row">
          <span className="label reduced-width">{getLabel(isAnalysis ? "analysisResults" : "substanceTreatments")}</span>
          <span className="value">
            {detailedSubstances.map((r) => (
              <div key={`analysis_${r.substance_id}`} className="tank-details-row-sub">
                <span className="sub-label">{r.name}</span>

                <span className="sub-value">
                  {r.value} {!isAnalysis && r?.quantity} {r.unit}

                  {/* Range validation icon */}
                  {isAnalysis && <span
                    className={`range-status ${
                      r.isInside ? "range-ok" : "range-bad"
                    }`}
                    title={
                      r.isInside
                        ? getLabel("valueInsideRange")
                        : getLabel("valueOutsideRange")
                    }
                  ></span>}
                </span>
              </div>
            ))}
          </span>
        </div>
      </>
    );
  };

  const renderStabilizationBlock = () => {
    const stabilization = op?.treatments?.[0] || null;
    if (!stabilization) return;

    const isStaticStabilization = stabilization?.type === "STABILIZATION_STATIC";
    const isDynamicStabilization = stabilization?.type === "STABILIZATION_DYNAMIC";
    const isResinStabilization = stabilization?.type === "STABILIZATION_RESIN";

    const { hours, minutes, formatted, className } = getElapsedTimeWithClass(
      new Date(op?.start_date),
      new Date(op?.completion_date),
      true,
      true
    );

    let stabilizationStatus = null;
    let realDuration = null;
    let estimatedDuration = null;
    let estimatedFormatted = null;
    
    if (isStaticStabilization) {
      // expected work time in days
      const expectedHours = Number(stabilization?.work_time) * 24;
      const totalHours = hours + (minutes / 60)
      const diff = Math.abs(totalHours - expectedHours);
      stabilizationStatus = (diff < 2) ? "range-ok" : (diff >= 2 && diff <= 5) ? "range-warning" : "range-bad";
      estimatedDuration = stabilization?.work_time || "-";
      realDuration = formatted;
    } else if (isDynamicStabilization) {
      // Ensure numeric values (default to 0)
      const iterations = Number(stabilization?.iter_per_day) || 0;
      const pause = Number(stabilization?.pause_time) || 0;
      const work = Number(stabilization?.work_time) || 0;
    
      // Total time in hours and minutes
      const totalPauseHours = iterations * pause;
      const totalWorkMinutes = iterations * work;
      const expectedHours = totalPauseHours + (iterations * totalWorkMinutes / 60);

      const totalHours = hours + (minutes / 60)
      const diff = Math.abs(totalHours - expectedHours);
      stabilizationStatus = (diff < 2) ? "range-ok" : (diff >= 2 && diff <= 5) ? "range-warning" : "range-bad";
      estimatedDuration = Math.round(expectedHours);
      realDuration = formatted;
      estimatedFormatted = `${stabilization?.work_time}m:${stabilization?.pause_time}h  x${stabilization?.iter_per_day} @${stabilization?.motor_speed}%`;
    } else if (isResinStabilization) {
      realDuration = formatted;
    }

    return (
      <>

        {/* STABILIZATION TYPE */}
        <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationType")}</span>
          <span className="value">{getLabel(`physicalProtocol${stabilization?.type}`)} {stabilization?.stabilization_type === "STABILIZATION_COLD" ? getLabel("physicalProtocolSTABILIZATION_COLDString") : ""}</span>
        </div>

        {/* TEMPERATURE */}
        {(isStaticStabilization || isDynamicStabilization) && <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationTemperature")}</span>
          <span className="value">{`${stabilization.temperature || ""}${"\u00b0"} C`}</span>
        </div>}
        
        {/* START DATE */}
        <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationStartDate")}</span>
          <span className="value">
            {op.start_date
              ? format(new Date(op.start_date), configs.hourMediumDateFormat)
              : "-"}
          </span>
        </div>

        {/* ESTIMATED DURATION */}
        {(isStaticStabilization || isDynamicStabilization) && <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationWorkEstimatedTimeString")}</span>
          <div className={isDynamicStabilization && "multi-line-wrapper"}>
            <span className="value">{isDynamicStabilization ? "≈" : ""}{estimatedDuration} {getLabel(isDynamicStabilization ? 
            (estimatedDuration > 1
                ? "repeatSize_HOUR"
                : "repeatSize_1HOUR"
            ) : (
              Number(stabilization?.work_time) > 1
                ? "repeatSize_DAY"
                : "repeatSize_1DAY"
            ))?.toLowerCase?.()}
            </span>
            {isDynamicStabilization && <span className="value">
              {estimatedFormatted}
            </span>}
          </div>
        </div>}

        {/* REAL DURATION */}
        {(isStaticStabilization || isDynamicStabilization) && <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationWorkRealTimeString")}</span>
          <span className="value">{realDuration}
            {/* Range validation icon */}
            <span
              className={`range-status ${stabilizationStatus}`}
              title={
                stabilizationStatus === "range-ok"
                  ? getLabel("valueInsideRange")
                  : getLabel("valueOutsideRange")
              }
            ></span>
          </span>
        </div>}

        {/* REAL RESIN DURATION */}
        {isResinStabilization && <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationResinWorkTime")}</span>
          <span className="value">{realDuration}</span>
        </div>}

        {/* PH GOAL RESIN */}
        {isResinStabilization && <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationPh")}</span>
          <span className="value">{stabilization?.stabilization_ph}</span>
        </div>}

        {/* WINE PERCENTAGE RESIN */}
        {isResinStabilization && <div className="tank-details-row">
          <span className="label">{getLabel("operationPhysicalTreatmentPerc")}</span>
          <span className="value">{stabilization?.quantity || "-"}% <span className="sub-value">{getLabel("ofPercentage")} {src?.old_quantity || "-"}L</span></span>
        </div>}

      </>
    );
  };

  const renderFilteringBlock = () => {
    const filters = op?.treatments || null;
    if (!filters) return;

    const filtering = op?.treatments?.[0] || null;
    if (!filtering) return;

    const tankData = op?.dest_tanks?.[0] || {};
    const liters = (Number(tankData?.quantity) || 0) + Number(tankData?.drop) || 0;

    const { hours, minutes, formatted } = getElapsedTimeWithClass(
      new Date(op?.start_date),
      op?.completion_date ? new Date(op?.completion_date) : new Date(),
      true,
      true,
    );
    const { l_per_min, l_per_hour} = computeFlowRates(liters, hours, minutes);

    return (
      <>

        {/* START DATE */}
        <div className="tank-details-row">
          <span className="label">{getLabel("stabilizationStartDate")}</span>
          <span className="value">
            {op.start_date
              ? format(new Date(op.start_date), configs.hourMediumDateFormat)
              : "-"}
          </span>
        </div>

        {/* REAL DATE */}
        <div className="tank-details-row">
          <span className="label">{getLabel("filteringWorkTime")}</span>
          <span className="value">{formatted}</span>
        </div>
        
        {/* FLOW RATE */}
        <div className="tank-details-row">
          <span className="label">{getLabel("flowRate")}</span>
          <span className="value">{l_per_min ? l_per_min : ''} {l_per_min ? 'L/min' : ''}</span>
        </div>
        
        {/* FILTERS */}
        <div className="tank-details-row">
          <span className="label">{getLabel(filters?.length > 1 ? "filteringFilters" : "filteringFilter")}</span>
          <div className="multi-line-wrapper">
            {filters.map((f) => (
                <span className="value">{f?.name || ""} {f?.serial_number && <span className="sub-value">{`(${f.serial_number})`}</span>}</span>
            ))}
          </div>
        </div>

        {/* CANDLES */}
        {filtering?.type === "HOUSING" && <div className="tank-details-row">
          <span className="label">{getLabel(filters?.length > 1 ? "filterCandles" : "filterCandle")}</span>
          <div className="multi-line-wrapper">
            {filtering?.candles?.map((c) => (
                <span className="value">{c?.serial_number || ""} {c?.type && <span className="sub-value">{`(${getLabel(`filterCandleType_${c.type}`)?.toLowerCase?.()})`}</span>}</span>
            ))}
          </div>
        </div>}
        
      </>
    );
  };

  const renderCleaningBlock = () => {
    if (!op?.winemaking_protocol_id) return;
    const cips = op?.treatments || null;
    if (!cips) return;

    const cleaningProtocol =
      protocols?.find((a) => a.id === op.winemaking_protocol_id) || null;
    if (!cleaningProtocol) return;

    const cipOperations = cips?.sort((a, b) => a?.day - b?.day)?.map( c => { return {
      // ...c,
      operation: c?.operation,
      end_day: ` (${c?.day}`,
      name: c?.substance_id === 45 ? getLabel("protocolProduct_H2O") : c?.substance_name,
      type: c?.operation !== "CLEANING" ? ` (${getLabel(`protocolOperation_${c?.operation}Short`)?.toLowerCase?.()})` : "",
      work_time: `${c?.work_time} min`,
      quantity: c?.operation === "CLEANING" ? `${c?.quantity}%` : "",    // no RINSE or VAPORIZATION
    }});
    
    return (
      <>

        {/* PROTOCOL */}
        <div className="tank-details-row">
          <span className="label">{getLabel("wineMakingCIPProtocols")}</span>
          <span className="value">{cleaningProtocol.name}</span>
        </div>

        {/* CIP TREATMENTS */}
        <div className="tank-details-row">
          <span className="label more-reduced-width">{getLabel("protocolStep")}</span>
          <div className="multi-line-wrapper">
            {cipOperations?.sort((a, b) => a?.day < b?.day).map((c) => (
                <span className="value">
                  {c?.name || ""}
                  <span className="sub-value">{c?.type || ""} {c?.quantity || ""} x{c?.work_time}
                  </span>
                  <span className="value italic">{c?.end_day || ""}</span>
                </span>
            ))}
          </div>
        </div>
        
      </>
    );
  };

  const renderClosingBlock = () => {
    const dests = op?.dest_tanks || [];
    if (!dests.length) return null;
    
    // const analysisProtocol =
    //   analysis?.find((a) => a.protocols_id === exam.protocol_id) || null;
    // if (!analysisProtocol) return null;

    return (
      <>

        {/* PROTOCOL */}
        {/* <div className="tank-details-row">
          <span className="label">{getLabel("analysisProtocols")}</span>
          <span className="value">{analysisProtocol.protocols_name}</span>
        </div> */}

        {/* CLOSING DETAILS */}
        <div className="tank-details-row">
          <span className="label reduced-width">{getLabel(dests?.length > 1 ? "operationDestBottleNames" : "operationDestBottleName")}</span>
          <span className="value">
            {dests.map((r) => (
              <div key={`closing_${r.transfer_id}`} className="tank-details-row-sub">
                <span className="sub-label">{r.batch_name || ""}</span>

                <span className="sub-value">
                  {r.cap_lot || ""} {r.bottle_lot || ""}
                </span>
              </div>
            ))}
          </span>
        </div>
      </>
    );
  };

  const computeTankNames = () => {
    if (type !== "lot") return;
    const involvedTanks = [...op?.src_tanks, ...op?.dest_tanks];
    const filteredTanks = involvedTanks?.filter((l) => l.batch_id === Number(id));

    return (
      <div className="tank-details-row">
        <span className="label reduced-width">{getLabel(filteredTanks?.length > 1 ? "tanks" : "tankName")}</span>
        <span className="value">
          {filteredTanks.map((r, i) => (
            <div key={`tank_${i}`} className="tank-details-row-sub tank_name">
              <span className="sub-label">{r?.name || ""}</span>
            </div>
          ))}
        </span>
      </div>
    );
  }

  const renderOperationContent = () => {
    if (!src) return null;
    const { drop, dropUnit, dropLabel } = computeDrop();
    const { drop: opDrop, dropUnit: opDropUnit, dropLabel: opDropLabel } = computeDrop(type === "lot");
    const isShowQuantity = (
      [
        "RACKING",
        "CUT",
        "DECANT",
        "DESTEMMING",
        "NEW_LOT",
        "FILTERING",
        "CLOSING",
      ].includes(op?.operation_type) 
      // && type === "tank"
    );

    const lotNameObject = {
      label: getLabel("lotName"),
      value: src.batch_name || "-",
    };
    const lotState = src?.state ? getLabel(`lotState_${src.state}`) : "";
    const lotType = ["WINE", "BOTTLED"].includes(src?.state) ? getLabel(`lotType${src.batch_type}`) : "";
    const lotQuality = src?.quality ? `, ${getLabel(`lotQuality_${src.quality}`)} ${getLabel("lotQuality")}` : "";
    const lotTypeObject = {
      label: getLabel("lotDetails"),
      value: (`${lotState}${lotType?.toLowerCase?.()}${lotQuality?.toLowerCase?.()}`),
    };
    const lotData = [ lotNameObject, lotTypeObject ];

    // in case of tank, show only one. For lot there may be multiple tanks
    const tankNames = computeTankNames();

    const rows = [
      ...(type === "tank" ? [{ label: getLabel("tankName"), value: src.name }] : []),
      // { label: getLabel("lotState"), value: getLabel(`lotState_${tank.state}`) },
      ...(["DESTEMMING", "NEW_LOT"].includes(op.operation_type) ? [{
        label: getLabel("lotColor"),
        value: getLabel(`lotColor_${src.color}`),
      }] : []),
      // ...(isShowQuality ? [{
      //   label: getLabel("lotQuality"),
      //   value: getLabel(`lotQuality_${tank.quality}`),
      // }] : []),
      ...(isShowQuantity ? [{ label: getLabel("lotQuantity"), value: `${src.quantity} L` }] : []),
      // { label: "Capacity", value: `${tank.capacity} L` },
      ...(op.operation_type !== "CLEANING" ? lotData : []),
      // ...(["WINE", "BOTTLED"].includes(tank?.state) && !["FILTERING"].includes(op?.operation_type)
      //   ? [
      //       {
      //         label: getLabel("lotType"),
      //         value: getLabel(`lotType${tank.batch_type}`),
      //       },
      //     ]
      //   : []),
      ...(drop > 0 ? [{ label: dropLabel, value: `${drop} ${dropUnit}` }] : []),
      ...((opDrop > 0 && ["DESTEMMING", "RACKING"].includes(op?.operation_type)) ? [{ label: opDropLabel, value: `${opDrop} ${opDropUnit}` }] : []),
    ];

    // Composition list
    const composition = src.composition?.map((c, i) => {
      const v = c.variety || "";
      const y = c.year ? ` [${c.year}]` : "";
      const o = c.origin ? `, ${c.origin}` : "";
      const p =
        c.percentage != null && c.percentage != 100
          ? ` (${c.percentage}%)`
          : "";
      return (
        <div key={`comp_${i}`} className="tank-details-row">
          <span className="label">{i === 0 ? getLabel("lotOrigins") : ""}</span>
          <span className="value">{`${v}${p}${o}${y}`}</span>
        </div>
      );
    });

    return (
      <div className="tank-details-container">
        {/* Header */}
        <div className={`tank-details-title ${op.direction}`}>
          <span>{getLabel(`operationType${op.operation_type}`)} {(op.operation_type === "FILTERING" && op?.treatments?.[0]?.type) ? getLabel(`filterType_${op?.treatments?.[0]?.type}One`)?.toLowerCase?.() : ""}</span>

          <div className={`date-block ${op?.direction}`}>
            <span className="date">
              {format(
                new Date(op?.completion_date),
                configs.hourShortDateFormat
              )}
            </span>
            <span className="user">{op?.completed_by_username}</span>
          </div>
        </div>

        {/* Rows */}
        <div className="tank-details-rows">

          {/* Tanks */}
          {type === "lot" && tankNames}

          {rows.map((r, idx) => (
            <div className="tank-details-row" key={idx}>
              <span className="label">{r.label}</span>
              <span className="value">{r.value}</span>
            </div>
          ))}

          {/* Composition */}
          {composition}

          {/* ANALYSIS / TREATMENT BLOCK */}
          {["ANALYSIS", "TREATMENT", "BOTTLING"].includes(op?.operation_type) &&
            renderAnalysisBlock()}

          {/* BOTTLING BLOCK */}
          {op?.operation_type === "BOTTLING" &&
            renderClosingBlock()}

          {/* STABILIZATION_STATIC / STABILIZATION_DYNAMIC / STABILIZATION_RESIN BLOCK */}
          {["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(op?.operation_type) &&
            renderStabilizationBlock()}

          {/* FILTERING */}
          {op?.operation_type === "FILTERING" &&
            renderFilteringBlock()}

          {/* CLEANING */}
          {op?.operation_type === "CLEANING" &&
            renderCleaningBlock()}

          {/* OPERATION RECAP */}
          {type === "tank" && <div className="tank-details-recap">{renderOperationRecap()}</div>}

          {/* CTA */}
          <div className="tank-details-cta">
            <NavLink
              to={`/programming/history/${op?.operation_type?.toLowerCase?.()}/${
                op?.operation_id
              }`}
              target="_blank"
              rel="noopener noreferrer"
              ><CTA customClassName={"add"}
              >{getLabel("goToOperationDetail")}
              </CTA>
            </NavLink>
          </div>
        </div>
      </div>
    );
  };

  if (op.virtual) {
    return (
      <div className="timeline-element virtual">
        <div className="timeline-icon-wrapper">
          <div className="timeline-icon small">
            {["INITIAL_BATCH", "BATCH_CHANGE"].includes(
              op?.virtual_operation_type
            ) && <FontAwesomeIcon icon="fa-tag" size="2xs" />}
          </div>
        </div>
        <p className="timeline-virtual-label">
          {op.label}
          {op.value && (
            <span className="timeline-virtual-label bold">{op.value}</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="timeline-element" onClick={handleClick}>
      {/* Icon + operation_type */}
      <div className="timeline-icon-wrapper">
        <div className="timeline-type">
          {getLabel(`operationType${op.operation_type}`)}
        </div>
        <div className="timeline-arrow-wrapper">
          <FontAwesomeIcon
            className={`timeline-arrow ${isSrc ? "out" : "in"}`}
            icon={"fa-arrow-left"}
          />
        </div>
        <div
          className={`timeline-icon ${isOpen ? "open" : ""} ${src?.color?.toLowerCase?.() || "red"}`}
        >
          <FontAwesomeIcon
            icon={getIconByOperationType(op?.operation_type)}
            size="sm"
          />
        </div>
      </div>

      {/* Date */}
      <div className={`timeline-date ${op.showTooltip ? "active" : ""}`}>
        {format(
          new Date(op?.completion_date) || null,
          configs.hourMediumDateFormat
        )}
      </div>

      {/* Content panel */}
      <div className={`timeline-content ${op.direction} ${isOpen ? "visible" : ""}`}>
        {renderOperationContent(op)}
      </div>
    </div>
  );
});

const Timeline = ({ type = "tank", dates = {}, isLoading, setIsLoading }) => {
  const { id } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const [getLabel] = useLabels();
  const activeWinery = useSelector(selectActiveWinery);
  const users = useSelector(selectUsers);
  const units = useSelector(selectUnits);
  const winemakings = useSelector(selectWineMaking);
  const treatments = useSelector(selectTreatments);
  const analysis = useSelector(selectAnalysis);
  const configs = useSelector(selectConfigs);
  const drugs = useSelector(selectDrugs);
  const storedAnalysisResults = useSelector(selectStoredAnalysisResults) || [];
  const [openId, setOpenId] = useState(null);
  const [expandAll, setExpandAll] = useState(null);
  const [timelineOperations, setTimelineOperations] = useState([]);
  const [batchNames, setBatchNames] = useState({});
  const [openItems, setOpenItems] = useState(new Set());

  useEffect(() => {
    async function fetchTracking() {
      await initData();

      if (analysis?.length === 0) {
        loadAnalysis();
      }
      const operations =
        type === "tank"
          ? await getTankTracking(axiosPrivate, id, dates)
          : await getLotTracking(axiosPrivate, id, dates);

      // await initResults(operations);

      const ops = [];
      operations
        ?.sort(
          (b, a) => new Date(b?.completion_date) - new Date(a?.completion_date)
        )
        ?.map((o, i) => {
          const tmp = {
            ...o,
            ...(o?.repetitive &&
              o?.main_id !== null && { hideRepetitive: true, opened: false }),
          };
          ops.push(tmp);
        });

      const opsWithState = ops?.map((op, index) => ({
        ...op,
        showTooltip: op.operation_id === openId,
        direction: (index % 2 === 0) ? 'left' : 'right',
        completed_by_username:
          users?.find?.((user) => Number(user.id) === Number(op?.completed_by))
            ?.username || "",
      }));
      const enrichedOps = enrichTankTimelineWithBatches(opsWithState);

      setTimelineOperations(enrichedOps);
      
      setIsLoading(false);

      const batch_names = {};
      operations
        ?.filter((op) => !op.repetitive)
        ?.forEach((op, index) => {
          const operation = op?.dest_tanks?.find((t) => t.tank_id == id) || {};
          const operationSrc =
            op?.src_tanks?.find((t) => t.tank_id == id) || null;
          if ("new_batch_name" in operation && operation?.new_batch_name) {
            batch_names[op?.operation_id] = {
              name: operation?.new_batch_name,
              type: operation?.new_batch_type,
              color:
                operation?.postOperation?.batch?.color?.toLowerCase() || "red",
            };

            if (Number(operation?.old_quantity) === 0 && index === 0) {
              batch_names[op?.operation_id].isEmpty = true;
              batch_names[op?.operation_id].isFirst = true;
            }
          } else if (operationSrc !== null) {
            // is a src, no new_batch_name in src so put it manually
            batch_names[op?.operation_id] = {
              name: operationSrc?.postOperation?.batch?.batch_name,
              type: "OLD",
              color:
                operationSrc?.postOperation?.batch?.color?.toLowerCase() ||
                "red",
            };
            if (Number(operationSrc?.postOperation?.tank?.quantity) === 0)
              batch_names[op?.operation_id].isEmpty = true;
          }
        });
      setBatchNames(batch_names);
    }

    fetchTracking();
  }, []);

  useEffect(() => {
    if (!expandAll) {
      // collapse all → empty Set
      setOpenItems(new Set());
    } else {
      // open all → fill Set with ALL timeline op ids
      const allOpIds = (timelineOperations || [])
        .map(o => o?.operation_id)        // must match TimelineElement key!
        .filter(Boolean);

      setOpenItems(new Set(allOpIds));
    }

  }, [expandAll, timelineOperations]);

  const initData = async () => {
    const promiseArray = [];

    if (units?.length === 0) {
      promiseArray.push(loadUnits());
    }
    if (users?.length === 0) {
      promiseArray.push(loadUsers());
    }
    if (drugs?.length === 0) {
      promiseArray.push(loadDrugs());
    }
    if (treatments?.length === 0) {
      promiseArray.push(loadTreatments());
    }
    if (winemakings?.length === 0) {
      promiseArray.push(loadWinemakingProtocols());
    }

    return Promise.all(promiseArray);
  };

  const loadWinemakingProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadUnits = async () => {
    const currentUnits = await getUnits(axiosPrivate);
    dispatch(setUnits(currentUnits));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  // const loadTanks = async () => {
  //   const currentTanks = await getTanks(activeWinery?.id, axiosPrivate);
  //   dispatch(setTanks(currentTanks));
  // };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  // const loadLots = async () => {
  //   const currentLots = await getLots(activeWinery?.id, axiosPrivate);
  //   dispatch(setLots(currentLots));
  // };

  const loadAnalysis = async () => {
    const currentAnalysis = await getProtocols("analysis", axiosPrivate);
    dispatch(setAnalysis(currentAnalysis));
  };

  const handleToggle = (opId) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(opId)) {
        next.delete(opId);
      } else {
        next.add(opId);
      }
      return next;
    });
  };

  const renderMarker = (isStart = true) => {
    if (!Array.isArray(timelineOperations) || timelineOperations?.length === 0)
      return;

    const dateIndex = isStart ? 0 : timelineOperations?.length - 1;
    const tmpDate = new Date(timelineOperations?.[dateIndex]?.completion_date);
    const date = isStart
      ? dates?.startDate < tmpDate
        ? tmpDate
        : dates?.startDate
      : dates?.endDate > tmpDate
      ? dates?.endDate
      : tmpDate;
    
    const isValid = date instanceof Date && !isNaN(date.getTime());
    if (!isValid) return;

    return (
      <div className={`timeline-marker ${isStart ? "start" : "end"}`}>
        {!isStart && (
          <div className="timeline-icon">
          <FontAwesomeIcon
            icon="fa-calendar"
            className="timeline-icon"
            size="sm"
          />
        </div>
        )}
        {date && <span className="timeline-date-label">
          {format(date, configs.shortDateFormat)}
        </span>}
        {isStart && (
        <div className="timeline-icon">
          <FontAwesomeIcon
            icon="fa-calendar"
            className="timeline-icon"
            size="sm"
          />
        </div>
        )}
      </div>
    );
  };

  const toggleOpenAll = () => {

  }

  const enrichTankTimelineWithBatches = (operations) => {
    if (type !== "tank" || operations.length === 0) return operations;

    const enriched = [];

    // ---- INITIAL STATE ----------------------------------------------------
    const firstOp = operations[0];
    const srcTank = firstOp?.src_tanks?.find((t) => t.tank_id === Number(id));
    const tank =
      (srcTank
        ? srcTank
        : firstOp?.dest_tanks?.find((t) => t.tank_id === Number(id))) || null;
    // if (!tank) continue;

    const old_batch_id = tank?.old_batch_id || null;
    const old_batch_name = tank?.old_batch_name || null;
    const old_quantity = Number(tank?.old_quantity) ?? 0;

    if (old_quantity === 0) {
      enriched.push({
        id: `initial_empty`,
        virtual: true,
        virtual_operation_type: "INITIAL_EMPTY",
        label: getLabel("tankState_EMPTY"),
      });
    } else {
      enriched.push({
        id: `initial_batch`,
        virtual: true,
        virtual_operation_type: "INITIAL_BATCH",
        label: `Initial batch: ${old_batch_name} (${old_quantity} L)`,
      });
    }
    // -----------------------------------------------------------------------

    // ---- PROCESS ALL OPERATIONS ------------------------------------------
    let prevBatchId = old_batch_id;

    for (const op of operations) {
      enriched.push(op);
      const srcTank = op?.src_tanks?.find((t) => t.tank_id === Number(id));
      const tank =
        (srcTank
          ? srcTank
          : op?.dest_tanks?.find((t) => t.tank_id === Number(id))) || null;
      if (!tank) continue;

      const new_batch_id = tank?.batch_id || null;
      const new_batch_name = tank?.batch_name || null;
      const new_quantity = Number(tank?.quantity) ?? 0;

      const isWineMoving = [
        "CUT",
        "DECANT",
        "RACKING",
        "DESTEMMING",
        "BOTTLING",
        "CLOSING",
        "NEW_LOT",
        "FILTERING",
      ].includes(op.operation_type);

      if (!isWineMoving) continue;

      // EMPTY TANK AFTER OP
      if (new_quantity === 0) {
        enriched.push({
          id: `empty_${op.operation_id}`,
          virtual: true,
          virtual_operation_type: "EMPTY_TANK",
          label: getLabel("tankState_EMPTY"),
        });
        prevBatchId = null;
        continue;
      }

      // BATCH CHANGE AFTER OP
      if (new_batch_id !== prevBatchId && new_batch_name !== old_batch_name) {
        enriched.push({
          id: `batchchange_${op.operation_id}`,
          virtual: true,
          virtual_operation_type: "BATCH_CHANGE",
          label: `${getLabel("newBatch", { state: getLabel(`lotState_${tank?.state}`)?.toLowerCase?.() })}:`,
          value: new_batch_name || "",
        });
        prevBatchId = new_batch_id;
      }
    }

    return enriched;
  };

  if (isLoading)
    return (
      <div className="loader_wrapper">
        <Loader />
      </div>
    );

  if (timelineOperations?.length === 0)
    return (
      <h5 className="alert_no_info">{getLabel("operationHistoryNoData")}</h5>
    );
  console.log('timelineOperations', timelineOperations)

  return (
    <div>
      <div className="timeline-filters">
        <CTA customClassName="add" onClick={() => setExpandAll(prev => !prev)}>
          {getLabel(!expandAll ? "filtersExpandAll" : "filtersShrinkAll")}
        </CTA>
      </div>
      <div className="timeline-container">
        {/* Vertical line spanning everything */}
        <div className="timeline-line"></div>

        {/* Start marker */}
        {renderMarker()}

        {/* Timeline elements */}
        {timelineOperations.map((op, index) => (
          <TimelineElement
            key={op.operation_id}
            id={id}
            op={op}
            type={type}
            index={index}
            protocols={["CLEANING"].includes(op?.operation_type) ? winemakings : op?.operation_type === "ANALYSIS" ? analysis : treatments}
            units={units}
            onToggle={() => handleToggle(op.operation_id)}
            isOpen={openItems.has(op.operation_id)}
          />
        ))}

        {/* End marker */}
        {renderMarker(false)}
      </div>
    </div>
  );
};

export default Timeline;    